import React, { useState, useEffect } from 'react';
import { api } from '../api/client.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useTenantScope } from '../context/TenantScopeContext.jsx';
import {
    ALINEA_PROFILES,
    RADAR_ARCHETYPE_AXES,
    ARCHETYPE_SUPERVISOR_HINT,
} from '../utils/constants.js';

const PROFILE_KEYS = ['constante', 'impulsivo', 'analitico', 'social'];
import './Survey.css';
import { GlossaryTerm } from '../components/GlossaryTerm.jsx';
import ProfileEvolutionMastersPanel from '../components/ProfileEvolutionMastersPanel.jsx';

// ============================================================
// ENEAGRAM & HUMAN DESIGN CALCULATORS (Numerological approach)
// ============================================================

const ENNEAGRAM_TYPES = {
    1: { name: 'El Reformador', emoji: '⚖️', color: '#7B68EE', desc: 'Idealista, con principios, determinado' },
    2: { name: 'El Ayudante', emoji: '💛', color: '#FFB347', desc: 'Generoso, complaciente, posesivo' },
    3: { name: 'El Triunfador', emoji: '🏆', color: '#FF6B6B', desc: 'Adaptable, ambicioso, orientado al éxito' },
    4: { name: 'El Individualista', emoji: '🎨', color: '#9B59B6', desc: 'Expresivo, dramático, ensimismado' },
    5: { name: 'El Investigador', emoji: '🔬', color: '#3498DB', desc: 'Perceptivo, innovador, reservado' },
    6: { name: 'El Leal', emoji: '🛡️', color: '#27AE60', desc: 'Comprometido, orientado a seguridad' },
    7: { name: 'El Entusiasta', emoji: '🎉', color: '#F39C12', desc: 'Optimista, versátil, espontáneo' },
    8: { name: 'El Desafiante', emoji: '🦁', color: '#E74C3C', desc: 'Poderoso, dominante, protector' },
    9: { name: 'El Pacificador', emoji: '🕊️', color: '#1ABC9C', desc: 'Receptivo, tranquilizador, complaciente' },
};

const HD_TYPES = {
    manifestacion: { name: 'Manifestación', emoji: '⚡', color: '#E74C3C', desc: 'Inicia y lidera, energía de impacto' },
    generacion: { name: 'Generación', emoji: '🔥', color: '#F39C12', desc: 'Fuerza constante, responde a la vida' },
    proyeccion: { name: 'Proyección', emoji: '🎯', color: '#3498DB', desc: 'Guía y dirige, ve el panorama' },
    reflejo: { name: 'Reflejo', emoji: '🌙', color: '#9B59B6', desc: 'Espejo del grupo, evalúa el ambiente' },
};

function calcEnneagram(birthDate) {
    if (!birthDate) return null;
    const digits = birthDate.replace(/\D/g, '');
    let sum = 0;
    for (const d of digits) sum += parseInt(d);
    while (sum > 9) {
        let newSum = 0;
        for (const d of String(sum)) newSum += parseInt(d);
        sum = newSum;
    }
    return sum || 9;
}

function calcHumanDesign(birthDate) {
    if (!birthDate) return null;
    const d = new Date(birthDate);
    const day = d.getDate();
    const month = d.getMonth() + 1;
    const combo = (day + month) % 4;
    const types = ['manifestacion', 'generacion', 'proyeccion', 'reflejo'];
    return types[combo];
}

// ============================================================
// SVG RADAR CHART COMPONENT
// ============================================================

function RadarChart({ data, labels, size = 300 }) {
    const cx = size / 2;
    const cy = size / 2;
    const radius = size * 0.38;
    const levels = 5;
    const maxVal = Math.max(...data, 1);
    const n = data.length;

    const getPoint = (index, value) => {
        const angle = (Math.PI * 2 * index) / n - Math.PI / 2;
        const r = (value / maxVal) * radius;
        return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle) };
    };

    const gridLines = [];
    for (let l = 1; l <= levels; l++) {
        const points = [];
        for (let i = 0; i < n; i++) {
            const p = getPoint(i, (maxVal / levels) * l);
            points.push(`${p.x},${p.y}`);
        }
        gridLines.push(points.join(' '));
    }

    const dataPoints = data.map((v, i) => getPoint(i, v));
    const dataPath = dataPoints.map(p => `${p.x},${p.y}`).join(' ');

    const axisLines = [];
    for (let i = 0; i < n; i++) {
        const p = getPoint(i, maxVal);
        axisLines.push({ x1: cx, y1: cy, x2: p.x, y2: p.y });
    }

    const labelPoints = labels.map((label, i) => {
        const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
        const r = radius + 28;
        return { x: cx + r * Math.cos(angle), y: cy + r * Math.sin(angle), label };
    });

    return (
        <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', maxWidth: `${size}px`, margin: '0 auto', display: 'block' }}>
            {/* Grid */}
            {gridLines.map((pts, i) => (
                <polygon key={i} points={pts} fill="none" stroke="rgba(193,127,89,0.15)" strokeWidth="0.5" />
            ))}
            {/* Axes */}
            {axisLines.map((line, i) => (
                <line key={i} {...line} stroke="rgba(193,127,89,0.2)" strokeWidth="0.5" />
            ))}
            {/* Data polygon */}
            <polygon points={dataPath} fill="rgba(193,127,89,0.25)" stroke="#C17F59" strokeWidth="2" />
            {/* Data points */}
            {dataPoints.map((p, i) => (
                <circle key={i} cx={p.x} cy={p.y} r="4" fill="#C17F59" stroke="#fff" strokeWidth="1.5" />
            ))}
            {/* Labels */}
            {labelPoints.map((p, i) => (
                <text key={i} x={p.x} y={p.y} textAnchor="middle" dominantBaseline="central"
                    fill="var(--color-text-primary, #333)" fontSize="10" fontWeight="500">
                    {p.label}
                </text>
            ))}
        </svg>
    );
}

// ============================================================
// DONUT CHART COMPONENT
// ============================================================

function DonutChart({ segments, size = 200, label }) {
    const cx = size / 2;
    const cy = size / 2;
    const outerR = size * 0.42;
    const innerR = size * 0.26;
    const total = segments.reduce((s, seg) => s + seg.value, 0) || 1;
    let cumAngle = -Math.PI / 2;

    const paths = segments.map((seg, i) => {
        const angle = (seg.value / total) * Math.PI * 2;
        const startAngle = cumAngle;
        const endAngle = cumAngle + angle;
        cumAngle = endAngle;

        const largeArc = angle > Math.PI ? 1 : 0;
        const x1o = cx + outerR * Math.cos(startAngle);
        const y1o = cy + outerR * Math.sin(startAngle);
        const x2o = cx + outerR * Math.cos(endAngle);
        const y2o = cy + outerR * Math.sin(endAngle);
        const x1i = cx + innerR * Math.cos(endAngle);
        const y1i = cy + innerR * Math.sin(endAngle);
        const x2i = cx + innerR * Math.cos(startAngle);
        const y2i = cy + innerR * Math.sin(startAngle);

        const d = `M ${x1o} ${y1o} A ${outerR} ${outerR} 0 ${largeArc} 1 ${x2o} ${y2o} L ${x1i} ${y1i} A ${innerR} ${innerR} 0 ${largeArc} 0 ${x2i} ${y2i} Z`;
        return <path key={i} d={d} fill={seg.color} opacity="0.85"><title>{seg.label}: {seg.value}</title></path>;
    });

    return (
        <svg viewBox={`0 0 ${size} ${size}`} style={{ width: '100%', maxWidth: `${size}px`, margin: '0 auto', display: 'block' }}>
            {paths}
            <text x={cx} y={cy - 6} textAnchor="middle" fill="var(--color-text-primary, #333)" fontSize="22" fontWeight="700">{total}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" fill="var(--color-text-secondary, #666)" fontSize="10">{label}</text>
        </svg>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function AdminDiagnostico() {
    const { user } = useAuth();
    const { tenantId, needsTenant, selectedTenantName, isSuperAdmin: scopeIsSuperAdmin } = useTenantScope();
    const isSuperAdmin = user?.rol === 'superadmin';
    const [activeTab, setActiveTab] = useState('resumen');
    const [summary, setSummary] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [manadaData, setManadaData] = useState(null);
    const [evolutionMasters, setEvolutionMasters] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterSupervisor, setFilterSupervisor] = useState('all');
    const [editingBirthDate, setEditingBirthDate] = useState(null);
    const [birthDateValue, setBirthDateValue] = useState('');
    const [error, setError] = useState(null);

    // Edit Question Modal State
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [editForm, setEditForm] = useState({ id: null, question: '', options: [] });
    const [selectedArchetype, setSelectedArchetype] = useState(null);

    useEffect(() => {
        if (!isSuperAdmin && (activeTab === 'manada' || activeTab === 'preguntas')) {
            setActiveTab('manada-registro');
        }
    }, [isSuperAdmin, activeTab]);

    useEffect(() => {
        loadData();
    }, [activeTab, tenantId]);

    async function loadData() {
        setLoading(true);
        setError(null);
        try {
            if (activeTab === 'resumen') {
                const response = await api.getAdminSurveySummary();
                setSummary(response?.data || { feelings: [], profiles: [], supervisors: [], employees: [] });
            } else if (activeTab === 'preguntas') {
                const response = await api.getProfileQuestions('tenant');
                setQuestions(response?.data || []);
            } else if (activeTab === 'ciclos') {
                if (isSuperAdmin && needsTenant) {
                    setEvolutionMasters(null);
                } else {
                    const response = await api.getHumanCyclesAdmin();
                    const payload = response?.data ?? null;
                    setEvolutionMasters(
                        payload
                            ? {
                                  ...payload,
                                  masters: payload.masters || {
                                      reviewIntervalDays: payload.config?.settings?.reviewIntervalDays,
                                      calendarCycle: payload.config?.calendarCycle,
                                      currentSeason: payload.config?.activeSeason,
                                      seasons: payload.config?.seasons,
                                      configSource: payload.config?.source,
                                      coreQuestionsCount: 10,
                                      notes: [],
                                  },
                              }
                            : null
                    );
                }
            } else if (activeTab === 'manada-registro' || (activeTab === 'manada' && isSuperAdmin)) {
                const response = await api.getMapaManada();
                setManadaData(response?.data || { employees: [], areas: [], supervisors: [] });
            }
        } catch (err) {
            setError('Error al cargar los datos. Intenta recargar la página.');
            console.error('Error loading admin diagnostico:', err);
        } finally {
            setLoading(false);
        }
    }

    const handleEditClick = (q) => {
        setEditForm({
            id: q.id,
            question: q.question,
            options: typeof q.options === 'string' ? JSON.parse(q.options) : q.options,
            display_order: q.display_order,
            is_active: q.is_active
        });
        setEditingQuestion(q.id);
    };

    const handleSaveQuestion = async (e) => {
        e.preventDefault();
        try {
            if (editForm.id) {
                await api.updateProfileQuestion(editForm.id, editForm);
            } else {
                await api.createProfileQuestion(editForm);
            }
            setEditingQuestion(null);
            loadData();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleDeleteQuestion = async (id) => {
        if (!window.confirm('¿Seguro que deseas desactivar esta pregunta?')) return;
        try {
            await api.deleteProfileQuestion(id);
            loadData();
        } catch (err) {
            alert(err.message);
        }
    };

    const handleResetProfileQuestions = async () => {
        if (
            !window.confirm(
                '¿Restaurar la encuesta inicial con la plantilla ALINEA? Se desactivan las preguntas actuales de su empresa y se cargan de nuevo las predeterminadas.'
            )
        ) {
            return;
        }
        try {
            const response = await api.resetProfileQuestions();
            setQuestions(response?.data || []);
            setEditingQuestion(null);
        } catch (err) {
            alert(err.message || 'No se pudo restaurar la plantilla');
        }
    };

    const handleSaveBirthDate = async (empId) => {
        try {
            await api.updateBirthDate(empId, birthDateValue);
            setEditingBirthDate(null);
            setBirthDateValue('');
            loadData();
        } catch (err) {
            alert(err.message);
        }
    };

    if (loading && !summary && !questions.length && !manadaData) {
        return <div className="loading-screen"><div className="spinner" /></div>;
    }

    // ============================================================
    // MAPA DE LA MANADA — Process data
    // ============================================================
    let manadaEmployees = [];
    let supervisorGroups = {};
    let enneagramDist = {};
    let hdDist = {};
    let profileDist = {};

    if (manadaData) {
        // Remove duplicates by employee ID to ensure unique keys
        const uniqueEmployees = manadaData.employees.filter((emp, index, self) => 
            index === self.findIndex(e => e.id === emp.id)
        );
        
        manadaEmployees = uniqueEmployees.map(emp => ({
            ...emp,
            enneagram: calcEnneagram(emp.birth_date),
            humanDesign: calcHumanDesign(emp.birth_date),
        }));

        if (filterSupervisor !== 'all') {
            manadaEmployees = manadaEmployees.filter(e => String(e.supervisor_id) === filterSupervisor);
        }

        // Group by supervisor
        manadaEmployees.forEach(emp => {
            const sName = emp.supervisor_name || 'Sin Supervisor';
            if (!supervisorGroups[sName]) supervisorGroups[sName] = [];
            supervisorGroups[sName].push(emp);
        });

        // Distributions with Profile Fallbacks
        manadaEmployees.forEach(emp => {
            const profile = ALINEA_PROFILES[emp.profile_type];
            
            // Eneagram / V1
            const enneagramVal = emp.enneagram || (profile?.enneagram_number); 
            if (enneagramVal) {
                enneagramDist[enneagramVal] = (enneagramDist[enneagramVal] || 0) + 1;
            }

            // Human Design / V2
            const hdVal = emp.humanDesign || (profile?.v2_id);
            if (hdVal) {
                hdDist[hdVal] = (hdDist[hdVal] || 0) + 1;
            }

            // ALINEA Profiles
            if (emp.profile_type) {
                profileDist[emp.profile_type] = (profileDist[emp.profile_type] || 0) + 1;
            }
        });
    }

    const radarLabels = RADAR_ARCHETYPE_AXES.map((a) => a.label);

    const radarPerSupervisor = {};
    const radarPerArea = {};
    if (manadaData) {
        Object.entries(supervisorGroups).forEach(([sName, emps]) => {
            radarPerSupervisor[sName] = PROFILE_KEYS.map((k) => emps.filter((e) => e.profile_type === k).length);
        });

        const areaGroups = {};
        manadaData.employees.forEach((emp) => {
            const areaName = emp.area_name || 'Sin área';
            if (!areaGroups[areaName]) areaGroups[areaName] = [];
            areaGroups[areaName].push(emp);
        });
        Object.entries(areaGroups).forEach(([areaName, emps]) => {
            radarPerArea[areaName] = {
                data: PROFILE_KEYS.map((k) => emps.filter((e) => e.profile_type === k).length),
                headcount: emps.length,
            };
        });
    }

    const radarAreasSorted = Object.entries(radarPerArea).sort(
        (a, b) => b[1].headcount - a[1].headcount
    );

    return (
        <div className="container container-wide animate-in">
            <h1 className="mb-lg">Módulo 1: Diagnóstico</h1>
            {error && (
                <div className="glass-card" style={{ borderLeft: '4px solid var(--color-alert-red)', marginBottom: '1rem', padding: '1rem', background: 'rgba(255,230,230,0.9)' }}>
                    <strong>Error:</strong> {error}
                </div>
            )}

            <div className="tabs mb-lg" style={{ display: 'flex', gap: '0.75rem', borderBottom: '1px solid rgba(0,0,0,0.1)', paddingBottom: '0.5rem', flexWrap: 'wrap' }}>
                <button className={`btn ${activeTab === 'resumen' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setActiveTab('resumen')}>
                    📊 Resumen General
                </button>
                <button
                    className={`btn ${activeTab === 'ciclos' ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setActiveTab('ciclos')}
                >
                    🌿 Ciclos y estaciones
                </button>
                {!isSuperAdmin && (
                    <button
                        className={`btn ${activeTab === 'manada-registro' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setActiveTab('manada-registro')}
                    >
                        🐺 Registro de manada
                    </button>
                )}
                {isSuperAdmin && (
                    <>
                        <button
                            className={`btn ${activeTab === 'preguntas' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setActiveTab('preguntas')}
                        >
                            📝 Registro de manada (preguntas)
                        </button>
                        <button
                            className={`btn ${activeTab === 'manada' ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setActiveTab('manada')}
                        >
                            🗺️ Mapa de equipos
                        </button>
                    </>
                )}
            </div>

            {activeTab === 'ciclos' && (
                <ProfileEvolutionMastersPanel
                    data={evolutionMasters}
                    loading={loading}
                    needsTenant={needsTenant}
                    selectedTenantName={selectedTenantName}
                    isSuperAdmin={scopeIsSuperAdmin || isSuperAdmin}
                    onRefresh={loadData}
                />
            )}

            {/* ===== TAB: RESUMEN GENERAL ===== */}
            {activeTab === 'resumen' && summary && (
                <div className="admin-summary-view stagger">
                    <div className="grid-2 mb-xl" style={{ gap: '2rem' }}>
                        <div className="glass-card">
                            <h3>Estado de Ánimo Hoy</h3>
                            <div className="mt-md">
                                {summary.feelings.length === 0 && <p className="text-muted">No hay check-ins hoy.</p>}
                                {summary.feelings.map(f => (
                                    <div key={f.feeling} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                        <span style={{ textTransform: 'capitalize' }}>
                                            {f.feeling === 'bien' ? '😊 Bien' : f.feeling === 'regular' ? '😐 Regular' : '😞 Mal'}
                                        </span>
                                        <strong>{f.count}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="glass-card">
                            <h3>Perfiles ALINEA Detectados</h3>
                            <div className="mt-md">
                                {summary.profiles.map(p => (
                                    <div key={p.profile_type} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                        <span style={{ textTransform: 'capitalize' }}>{p.profile_type}</span>
                                        <strong>{p.count}</strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <h2 className="mb-md">Resumen por Supervisor</h2>
                    <div className="glass-card table-container mb-xl">
                        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
                                    <th style={{ padding: '1rem' }}>Líder</th>
                                    <th style={{ padding: '1rem' }}>Tamaño del Equipo</th>
                                    <th style={{ padding: '1rem' }}>Detección de Sombra (Semana)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.supervisors.map(s => (
                                    <tr key={s.supervisor_name} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                        <td style={{ padding: '1rem' }}>{s.supervisor_name}</td>
                                        <td style={{ padding: '1rem' }}>{s.team_size} trabajadores</td>
                                        <td style={{ padding: '1rem' }}>
                                            {s.mal_count > 0 ? (
                                                <span className="badge badge-red">{s.mal_count} en sombra</span>
                                            ) : (
                                                <span className="badge badge-green">En Eje</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <h2 className="mb-md">Check-ins de Hoy por Empleado</h2>
                    <div className="glass-card table-container">
                        <table style={{ width: '100%', textAlign: 'left', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid rgba(0,0,0,0.1)' }}>
                                    <th style={{ padding: '1rem' }}>Empleado</th>
                                    <th style={{ padding: '1rem' }}>Código</th>
                                    <th style={{ padding: '1rem' }}>Perfil ALINEA</th>
                                    <th style={{ padding: '1rem' }}>Presencia Hoy</th>
                                    <th style={{ padding: '1rem' }}>Alerta Biométrica</th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.employees.map(e => (
                                    <tr key={e.employee_code} style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                        <td style={{ padding: '1rem' }}>{e.name}</td>
                                        <td style={{ padding: '1rem' }}>{e.employee_code}</td>
                                        <td style={{ padding: '1rem', textTransform: 'capitalize' }}>{e.profile_type || 'Sin perfil'}</td>
                                        <td style={{ padding: '1rem' }}>
                                            {e.feeling === 'bien' ? '🧘 Eje' : e.feeling === 'regular' ? '⚖️ Esfuerzo' : e.feeling === 'mal' ? '🌑 Sombra' : 'Pendiente'}
                                        </td>
                                        <td style={{ padding: '1rem' }}>
                                            {e.is_inconsistent === 1 && (
                                                <span className="badge badge-yellow" style={{ backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #f59e0b' }}>⚠️ Incoherencia Somática</span>
                                            )}
                                            {e.feeling === 'mal' && !e.is_inconsistent && (
                                                <span className="badge" style={{ backgroundColor: '#fee2e2', color: '#b91c1c' }}>
                                                    <GlossaryTerm termId="carga_alostatica" label="Carga alostática" />
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ===== TAB: REGISTRO DE MANADA (admin empresa) — radar por área + arquetipos ===== */}
            {activeTab === 'manada-registro' && (
                <div className="manada-registro-view stagger">
                    {loading && !manadaData ? (
                        <p className="text-muted text-center py-lg">Cargando composición de manadas…</p>
                    ) : (
                        <>
                            <div className="glass-card mb-lg" style={{ padding: '1rem 1.25rem', borderLeft: '4px solid var(--color-primary)' }}>
                                <h2 style={{ margin: 0 }}>Registro de manada por áreas</h2>
                                <p className="text-muted mt-sm mb-0" style={{ lineHeight: 1.55, maxWidth: '52rem' }}>
                                    Vista de tu empresa: cómo se distribuyen los cuatro arquetipos ALINEA en cada
                                    área. Usa los radares para priorizar conversaciones con los líderes de zona.
                                </p>
                            </div>

                            <h2 className="mb-md">📡 Radar de arquetipos por área</h2>
                            {radarAreasSorted.length === 0 ? (
                                <p className="text-muted glass-card" style={{ padding: '1.5rem' }}>
                                    Aún no hay personas activas con perfil registrado en las áreas.
                                </p>
                            ) : (
                                <div
                                    style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                                        gap: '1.5rem',
                                        marginBottom: '2.5rem',
                                    }}
                                >
                                    {radarAreasSorted.map(([areaName, { data, headcount }]) => (
                                        <div key={areaName} className="glass-card" style={{ textAlign: 'center' }}>
                                            <h4 style={{ marginBottom: '0.35rem' }}>{areaName}</h4>
                                            <p className="text-muted" style={{ fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                                                {headcount} persona{headcount !== 1 ? 's' : ''} activa{headcount !== 1 ? 's' : ''}
                                            </p>
                                            <RadarChart data={data} labels={radarLabels} size={260} />
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: '0.5rem 1rem',
                                                    marginTop: '0.75rem',
                                                    fontSize: '0.75rem',
                                                }}
                                            >
                                                {RADAR_ARCHETYPE_AXES.map((axis, i) => (
                                                    <span key={axis.key}>
                                                        <strong>{data[i]}</strong> {axis.label}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <h2 className="mb-md">🧭 Guía de arquetipos ALINEA</h2>
                            <p className="text-muted mb-lg" style={{ maxWidth: '40rem' }}>
                                Cada perfil describe una forma habitual de aportar en el trabajo. No es una etiqueta
                                fija: sirve para dialogar con respeto y ajustar el liderazgo.
                            </p>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                                    gap: '1.25rem',
                                }}
                            >
                                {PROFILE_KEYS.map((key) => {
                                    const p = ALINEA_PROFILES[key];
                                    const axis = RADAR_ARCHETYPE_AXES.find((a) => a.key === key);
                                    return (
                                        <div
                                            key={key}
                                            className="glass-card"
                                            style={{
                                                padding: '1.25rem',
                                                borderTop: `4px solid ${axis?.color || 'var(--color-primary)'}`,
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', marginBottom: '0.75rem' }}>
                                                <span style={{ fontSize: '2rem' }}>{p.emoji}</span>
                                                <div>
                                                    <h4 style={{ margin: 0 }}>{p.name}</h4>
                                                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                        Eje: {axis?.label}
                                                    </span>
                                                </div>
                                            </div>
                                            <p style={{ fontSize: '0.88rem', lineHeight: 1.5, margin: '0 0 0.65rem' }}>
                                                {p.description}
                                            </p>
                                            <p style={{ fontSize: '0.82rem', margin: '0 0 0.5rem' }}>
                                                <strong>Arquetipo:</strong> {p.archetypeShort || p.archetype}
                                            </p>
                                            <p style={{ fontSize: '0.82rem', margin: '0 0 0.5rem', fontStyle: 'italic' }}>
                                                {p.message}
                                            </p>
                                            <p
                                                style={{
                                                    fontSize: '0.8rem',
                                                    margin: 0,
                                                    padding: '0.65rem',
                                                    background: 'var(--color-bg)',
                                                    borderRadius: '8px',
                                                    borderLeft: `3px solid ${axis?.color || '#0f766e'}`,
                                                }}
                                            >
                                                <strong>Liderazgo:</strong> {ARCHETYPE_SUPERVISOR_HINT[key]}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ===== TAB: GESTIÓN DE PREGUNTAS (solo súper admin) ===== */}
            {activeTab === 'preguntas' && isSuperAdmin && (
                <div className="admin-questions-view stagger">
                    <div className="glass-card mb-lg" style={{ padding: '1rem 1.25rem', borderLeft: '4px solid var(--color-primary)' }}>
                        <h2 style={{ margin: 0 }}>Preguntas por empresa (opcional / legado)</h2>
                        <p className="text-muted mt-sm mb-0" style={{ lineHeight: 1.55, maxWidth: '52rem' }}>
                            El <strong>Registro de manada</strong> que ven los trabajadores usa siempre la{' '}
                            <strong>plantilla oficial ALINEA</strong> (10 preguntas estándar). Esta sección sirve solo si
                            mantiene borradores por tenant; no reemplaza la encuesta del operario.
                        </p>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                        <span className="text-muted">{questions.length} preguntas activas</span>
                        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button type="button" className="btn btn-secondary" onClick={handleResetProfileQuestions}>
                                ↺ Restaurar plantilla ALINEA
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={() => {
                                    setEditForm({ id: null, question: '', options: [{ label: '', value: 1 }, { label: '', value: 2 }, { label: '', value: 3 }, { label: '', value: 4 }], display_order: questions.length + 1, is_active: true });
                                    setEditingQuestion('new');
                                }}
                            >
                                + Nueva pregunta
                            </button>
                        </div>
                    </div>

                    {editingQuestion && (
                        <div className="glass-card mb-xl animate-fade" style={{ border: '2px solid var(--color-primary)' }}>
                            <h3>{editingQuestion === 'new' ? 'Nueva Pregunta' : 'Editar Pregunta'}</h3>
                            <form onSubmit={handleSaveQuestion} style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1rem' }}>
                                <div>
                                    <label>Pregunta:</label>
                                    <input
                                        className="input mt-xs"
                                        value={editForm.question}
                                        onChange={e => setEditForm({ ...editForm, question: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="grid-2">
                                    {editForm.options.map((opt, idx) => (
                                        <div key={idx}>
                                            <label>Opción {idx + 1} (Perfil valor {idx + 1}):</label>
                                            <input
                                                className="input mt-xs"
                                                value={opt.label}
                                                onChange={e => {
                                                    const newOpts = [...editForm.options];
                                                    newOpts[idx].label = e.target.value;
                                                    setEditForm({ ...editForm, options: newOpts });
                                                }}
                                                required
                                            />
                                        </div>
                                    ))}
                                </div>
                                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                                    <button type="submit" className="btn btn-primary">Guardar Pregunta</button>
                                    <button type="button" className="btn btn-secondary" onClick={() => setEditingQuestion(null)}>Cancelar</button>
                                </div>
                            </form>
                        </div>
                    )}

                    <div className="questions-list" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        {questions.map(q => (
                            <div key={q.id} className="glass-card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <h4>{q.display_order}. {q.question}</h4>
                                    <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem', color: 'var(--color-text-secondary)' }}>
                                        {(typeof q.options === 'string' ? JSON.parse(q.options) : q.options).map((opt, i) => (
                                            <li key={i}>{opt.label}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <button className="btn btn-secondary" style={{ padding: '0.5rem' }} onClick={() => handleEditClick(q)}>✏️ Editar</button>
                                    <button className="btn btn-secondary" style={{ padding: '0.5rem', color: 'var(--color-alert-red)' }} onClick={() => handleDeleteQuestion(q.id)}>🗑️</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ===== TAB: MAPA DE LA MANADA ===== */}
            {activeTab === 'manada' && isSuperAdmin && manadaData && (
                <div className="manada-view stagger">

                    {/* Filter */}
                    <div className="glass-card mb-lg" style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                        <label style={{ fontWeight: 600 }}>🔍 Filtrar por Supervisor:</label>
                        <select className="input" style={{ maxWidth: '280px' }} value={filterSupervisor} onChange={e => setFilterSupervisor(e.target.value)}>
                            <option value="all">Todos los equipos</option>
                            {manadaData.supervisors.map(s => (
                                <option key={s.id} value={String(s.id)}>{s.full_name}</option>
                            ))}
                        </select>
                        <span className="text-muted" style={{ marginLeft: 'auto' }}>{manadaEmployees.length} personas</span>
                    </div>

                    {/* Distribution Charts Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem', marginBottom: '2rem' }}>
                        {/* ALINEA Profile Distribution */}
                        <div className="glass-card" style={{ textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>🧭 Perfiles ALINEA</h3>
                            <p className="text-muted" style={{ fontSize: '0.7rem', marginBottom: '1rem' }}>
                                Clasificación de afinidad conductual y modo de anclaje.
                            </p>
                            
                            {!selectedArchetype ? (
                                <div className="animate-fade">
                                    <DonutChart
                                        size={180}
                                        label="Total"
                                        segments={[
                                            { value: profileDist.constante || 0, color: '#6CBF84', label: 'Constante' },
                                            { value: profileDist.impulsivo || 0, color: '#E85D5D', label: 'Impulsivo' },
                                            { value: profileDist.analitico || 0, color: '#5B9BD5', label: 'Analítico' },
                                            { value: profileDist.social || 0, color: '#F7C948', label: 'Social' },
                                        ]}
                                    />
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                                        {[
                                            { key: 'constante', color: '#6CBF84', label: 'Constante' },
                                            { key: 'impulsivo', color: '#E85D5D', label: 'Impulsivo' },
                                            { key: 'analitico', color: '#5B9BD5', label: 'Analítico' },
                                            { key: 'social', color: '#F7C948', label: 'Social' },
                                        ].map(p => (
                                            <span 
                                                key={p.key} 
                                                onClick={() => setSelectedArchetype(p.key)}
                                                style={{ fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', background: 'rgba(0,0,0,0.03)' }}
                                            >
                                                <span style={{ width: 8, height: 8, borderRadius: '50%', background: p.color }} />
                                                {p.label}
                                            </span>
                                        ))}
                                    </div>
                                    <p className="text-muted" style={{ fontSize: '0.65rem', marginTop: '1rem' }}>Haz clic en un perfil para ver su ficha técnica.</p>
                                </div>
                            ) : (
                                <div className="animate-in" style={{ textAlign: 'left', background: 'rgba(255,255,255,0.5)', padding: '1rem', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                            <span style={{ fontSize: '2.5rem' }}>{ALINEA_PROFILES[selectedArchetype].emoji}</span>
                                            <div>
                                                <h4 style={{ margin: 0, color: 'var(--color-primary)' }}>{ALINEA_PROFILES[selectedArchetype].name}</h4>
                                                <span style={{ fontSize: '0.65rem', fontWeight: 700, opacity: 0.6 }}>FICHA DE PRECISIÓN</span>
                                            </div>
                                        </div>
                                        <button onClick={() => setSelectedArchetype(null)} style={{ border: 'none', background: 'none', fontSize: '1.2rem', cursor: 'pointer', opacity: 0.5 }}>✕</button>
                                    </div>

                                    <div style={{ fontSize: '0.85rem' }}>
                                        <div className="mb-sm">
                                            <strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#94a3b8' }}>🎯 Foco Operativo:</strong>
                                            <p style={{ margin: '2px 0 0' }}>
                                                {selectedArchetype === 'constante' && 'Estabilidad y protocolos claros.'}
                                                {selectedArchetype === 'impulsivo' && 'Velocidad y resolución de impacto.'}
                                                {selectedArchetype === 'analitico' && 'Precisión técnica y lógica de proceso.'}
                                                {selectedArchetype === 'social' && 'Armonía y resonancia del equipo.'}
                                            </p>
                                        </div>
                                        <div className="mb-sm">
                                            <strong style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: '#94a3b8' }}>💪 Fortaleza:</strong>
                                            <p style={{ margin: '2px 0 0' }}>
                                                {selectedArchetype === 'constante' && 'Mantiene calidad en pautas largas.'}
                                                {selectedArchetype === 'impulsivo' && 'Resolución veloz de crisis técnicas.'}
                                                {selectedArchetype === 'analitico' && 'Optimización y detección de fallas.'}
                                                {selectedArchetype === 'social' && 'Soporte y unión bajo estrés.'}
                                            </p>
                                        </div>
                                        <div style={{ padding: '0.75rem', background: 'var(--color-bg)', borderRadius: '8px', borderLeft: '3px solid var(--color-primary)' }}>
                                            <strong style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'var(--color-primary-dark)' }}>💡 Tip de Liderazgo:</strong>
                                            <p style={{ margin: '2px 0 0', fontStyle: 'italic', fontSize: '0.8rem' }}>
                                                {selectedArchetype === 'constante' && 'Valore su confiabilidad.'}
                                                {selectedArchetype === 'impulsivo' && 'Dele autonomía operativa.'}
                                                {selectedArchetype === 'analitico' && 'Explíquele el "por qué" técnico.'}
                                                {selectedArchetype === 'social' && 'Refuerce su sentido de pertenencia.'}
                                            </p>
                                        </div>
                                    </div>
                                    <button className="btn btn-secondary w-full mt-md" onClick={() => setSelectedArchetype(null)} style={{ fontSize: '0.75rem', minHeight: '36px' }}>
                                        Volver al gráfico
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Eneagram Distribution */}
                        <div className="glass-card" style={{ textAlign: 'center' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>🔮 Tu ritmo natural</h3>
                            <p className="text-muted" style={{ fontSize: '0.7rem', marginBottom: '1rem' }}>
                                <strong>Variable V1:</strong> Calibración de la tendencia conductual dinámica y respuesta ante presión.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(type => {
                                    const count = enneagramDist[type] || 0;
                                    const pct = manadaEmployees.length ? (count / manadaEmployees.length) * 100 : 0;
                                    const info = ENNEAGRAM_TYPES[type];
                                    return (
                                        <div key={type} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                                            <span style={{ width: '24px', textAlign: 'right', fontWeight: 700, color: info.color }}>{info.emoji}</span>
                                            <span style={{ width: '110px', textAlign: 'left', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={info.desc}>{info.name}</span>
                                            <div style={{ flex: 1, height: '16px', background: 'rgba(0,0,0,0.06)', borderRadius: '8px', overflow: 'hidden' }}>
                                                <div style={{
                                                    width: `${Math.max(pct, 2)}%`, height: '100%',
                                                    background: `linear-gradient(90deg, ${info.color}cc, ${info.color})`,
                                                    borderRadius: '8px', transition: 'width 0.5s ease',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '4px',
                                                    fontSize: '0.65rem', color: '#fff', fontWeight: 700
                                                }}>
                                                    {count > 0 && count}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-muted" style={{ fontSize: '0.6rem', marginTop: '0.75rem' }}>Datos calculados mediante biometría operativa y diagnóstico de precisión.</p>
                        </div>

                        {/* Human Design Distribution */}
                        <div className="glass-card" style={{ textAlign: 'center' }}>
                            <h3 style={{ marginBottom: '0.25rem' }}>⚡ Formas de actuar</h3>
                            <p className="text-muted" style={{ fontSize: '0.7rem', marginBottom: '1rem' }}>
                                <strong>Variable V2:</strong> Arquitectura de diseño que define el rol operativo y gestión de energía.
                            </p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                                {Object.entries(HD_TYPES).map(([key, info]) => {
                                    const count = hdDist[key] || 0;
                                    const pct = manadaEmployees.length ? (count / manadaEmployees.length) * 100 : 0;
                                    return (
                                        <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
                                            <span style={{ fontSize: '1.2rem' }}>{info.emoji}</span>
                                            <span style={{ width: '130px', textAlign: 'left', fontWeight: 500 }}>{info.name}</span>
                                            <div style={{ flex: 1, height: '20px', background: 'rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
                                                <div style={{
                                                    width: `${Math.max(pct, 3)}%`, height: '100%',
                                                    background: `linear-gradient(90deg, ${info.color}bb, ${info.color})`,
                                                    borderRadius: '10px', transition: 'width 0.5s ease',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '0.7rem', color: '#fff', fontWeight: 700
                                                }}>
                                                    {count}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <p className="text-muted" style={{ fontSize: '0.6rem', marginTop: '0.75rem' }}>Datos calculados mediante biometría operativa y diagnóstico de precisión.</p>
                        </div>
                    </div>

                    {/* Radar Charts per Supervisor */}
                    <h2 className="mb-md">📡 Radar de perfiles por equipo</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
                        {Object.entries(radarPerSupervisor).map(([sName, data]) => (
                            <div key={sName} className="glass-card" style={{ textAlign: 'center' }}>
                                <h4 style={{ marginBottom: '0.5rem' }}>🐺 {sName}</h4>
                                <p className="text-muted" style={{ fontSize: '0.8rem', marginBottom: '0.75rem' }}>
                                    {supervisorGroups[sName].length} miembros
                                </p>
                                <RadarChart data={data} labels={radarLabels} size={260} />
                                <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginTop: '0.75rem', fontSize: '0.75rem' }}>
                                    {radarLabels.map((l, i) => (
                                        <span key={l}><strong>{data[i]}</strong> {l}</span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Employee Cards */}
                    <h2 className="mb-md">👥 Directorio del equipo</h2>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: '1rem' }}>
                        {manadaEmployees.map((emp, index) => (
                            <div key={`emp-${emp.id}-${index}`} className="glass-card manada-emp-card" style={{ padding: '1rem' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                                    <div>
                                        <h4 style={{ margin: 0, fontSize: '0.95rem' }}>{emp.full_name}</h4>
                                        <p className="text-muted" style={{ fontSize: '0.75rem', margin: '2px 0' }}>{emp.area_name} · {emp.employee_code}</p>
                                    </div>
                                    <span style={{ fontSize: '1.3rem' }}>
                                        {emp.latest_feeling === 'bien' ? '😊' : emp.latest_feeling === 'regular' ? '😐' : emp.latest_feeling === 'mal' ? '😞' : '⬜'}
                                    </span>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem', fontSize: '0.7rem' }}>
                                    {/* ALINEA Profile */}
                                    <div style={{ textAlign: 'center', padding: '0.6rem 0.3rem', background: 'rgba(0,0,0,0.03)', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: '1.4rem', marginBottom: '0.2rem' }}>
                                            {ALINEA_PROFILES[emp.profile_type]?.emoji || '⬜'}
                                        </div>
                                        <div style={{ fontWeight: 800, color: 'var(--color-primary)', textTransform: 'uppercase', fontSize: '0.65rem', marginBottom: '2px' }}>
                                            {emp.profile_type || '—'}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.6rem', fontWeight: 600 }}>PERFIL MASTER</div>
                                    </div>

                                    {/* Variable V1 - Ritmo Natural */}
                                    <div style={{ textAlign: 'center', padding: '0.6rem 0.3rem', background: 'rgba(0,0,0,0.03)', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: '1.4rem', marginBottom: '0.2rem' }}>
                                            {emp.enneagram ? ENNEAGRAM_TYPES[emp.enneagram].emoji : (ALINEA_PROFILES[emp.profile_type]?.emoji || '❓')}
                                        </div>
                                        <div style={{ fontWeight: 700, fontSize: '0.65rem', marginBottom: '2px' }}>
                                            {emp.enneagram ? `TIPO ${emp.enneagram}` : (ALINEA_PROFILES[emp.profile_type]?.v1_id?.toUpperCase() || '—')}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.6rem', fontWeight: 600 }}>V1: TENDENCIA</div>
                                    </div>

                                    {/* Variable V2 - Arquitectura Operativa */}
                                    <div style={{ textAlign: 'center', padding: '0.6rem 0.3rem', background: 'rgba(0,0,0,0.03)', borderRadius: '10px', border: '1px solid rgba(0,0,0,0.05)' }}>
                                        <div style={{ fontSize: '1.4rem', marginBottom: '0.2rem' }}>
                                            {emp.humanDesign ? HD_TYPES[emp.humanDesign]?.emoji : (HD_TYPES[ALINEA_PROFILES[emp.profile_type]?.v2_id]?.emoji || '❓')}
                                        </div>
                                        <div style={{ fontWeight: 700, fontSize: '0.65rem', marginBottom: '2px' }}>
                                            {emp.humanDesign ? HD_TYPES[emp.humanDesign]?.name.toUpperCase() : (HD_TYPES[ALINEA_PROFILES[emp.profile_type]?.v2_id]?.name.toUpperCase() || '—')}
                                        </div>
                                        <div className="text-muted" style={{ fontSize: '0.6rem', fontWeight: 600 }}>V2: ROL OPERATIVO</div>
                                    </div>
                                </div>

                                {/* Birth date */}
                                <div style={{ marginTop: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem' }}>
                                    <span className="text-muted">🎂</span>
                                    {editingBirthDate === emp.id ? (
                                        <div style={{ display: 'flex', gap: '0.3rem', flex: 1 }}>
                                            <input type="date" className="input" style={{ fontSize: '0.75rem', padding: '0.25rem', flex: 1 }}
                                                value={birthDateValue} onChange={e => setBirthDateValue(e.target.value)} />
                                            <button className="btn btn-primary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                                                onClick={() => handleSaveBirthDate(emp.id)}>✓</button>
                                            <button className="btn btn-secondary" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem' }}
                                                onClick={() => setEditingBirthDate(null)}>✕</button>
                                        </div>
                                    ) : (
                                        <>
                                            <span>{emp.birth_date || 'Sin fecha'}</span>
                                            <button className="btn btn-secondary" style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem', marginLeft: 'auto' }}
                                                onClick={() => { setEditingBirthDate(emp.id); setBirthDateValue(emp.birth_date || ''); }}>
                                                ✏️
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {manadaEmployees.filter(e => !e.birth_date).length > 0 && (
                        <div className="glass-card mt-lg" style={{ borderLeft: '4px solid #F7C948', padding: '1rem' }}>
                            <p style={{ margin: 0, fontSize: '0.85rem' }}>
                                ⚠️ <strong>{manadaEmployees.filter(e => !e.birth_date).length} personas</strong> no tienen fecha de nacimiento. Agregala con el botón ✏️ para calcular su Ritmo natural y Formas de actuar.
                            </p>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
